export interface Landmark {
  x: number;
  y: number;
  z?: number;
  visibility?: number;
}

export interface HandLandmark {
  x: number;
  y: number;
  z?: number;
}

export interface PostureMetrics {
  /** Minimum normalized distance between any fingertip/hand and the face center. */
  handToFaceDistanceRatio: number;
  /** Which hand is closest to the face, if any. */
  activeHand: "left" | "right" | null;
  /** Which specific finger is touching (if any). */
  touchingFinger: "thumb" | "index" | "middle" | "ring" | "pinky" | "palm" | null;
  /** Which part of the face is being touched. */
  faceRegion: "nose" | "mouth" | "cheek" | "eye" | null;
}

export interface PostureQuality {
  /** True if bad posture is detected (head forward, shoulders hunched). */
  isBadPosture: boolean;
  /** Angle between vertical and head-to-shoulder line (degrees). Higher = more forward. */
  headForwardAngle: number | null;
  /** Confidence of posture assessment (0-1). */
  postureConfidence: number;
}

export interface PostureAssessment {
  metrics: PostureMetrics;
  /** True when a finger or hand appears to be touching or hovering very close to the face. */
  handTouchingFace: boolean;
  /** Average confidence of the keypoints used in the assessment (0-1). */
  confidence: number;
  /** Specific behavior detected. */
  behavior: "nose-picking" | "nail-biting" | "face-touching" | null;
  /** Body posture quality assessment. */
  postureQuality: PostureQuality;
}

// MediaPipe Pose Landmarker indices
const POSE_LANDMARKS = {
  NOSE: 0,
  LEFT_EYE_INNER: 1,
  LEFT_EYE: 2,
  LEFT_EYE_OUTER: 3,
  RIGHT_EYE_INNER: 4,
  RIGHT_EYE: 5,
  RIGHT_EYE_OUTER: 6,
  LEFT_EAR: 7,
  RIGHT_EAR: 8,
  MOUTH_LEFT: 9,
  MOUTH_RIGHT: 10,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
} as const;

const DEFAULT_MIN_VISIBILITY = 0.5;
const FINGER_DISTANCE_THRESHOLD = 0.28; // Increased to catch nail biting
const WRIST_DISTANCE_THRESHOLD = 0.35; // Fallback for wrist detection
const SHOULDER_BUFFER = 0.6;

// Posture detection thresholds
const BAD_POSTURE_ANGLE_THRESHOLD = 15; // Degrees - if head is more than 15° forward from vertical

// MediaPipe hand landmark indices for fingertips
const FINGERTIP_INDICES = {
  thumb: 4,
  index: 8,
  middle: 12,
  ring: 16,
  pinky: 20,
} as const;

type FingerName = keyof typeof FINGERTIP_INDICES;
type FaceRegion = "nose" | "mouth" | "cheek" | "eye" | null;

function determineFaceRegion(
  fingerX: number,
  fingerY: number,
  poseLandmarks: Landmark[],
  minVisibility: number,
  videoWidth: number,
  videoHeight: number
): FaceRegion {
  const nose = poseLandmarks[POSE_LANDMARKS.NOSE];
  const mouthLeft = poseLandmarks[POSE_LANDMARKS.MOUTH_LEFT];
  const mouthRight = poseLandmarks[POSE_LANDMARKS.MOUTH_RIGHT];
  const leftEye = poseLandmarks[POSE_LANDMARKS.LEFT_EYE];
  const rightEye = poseLandmarks[POSE_LANDMARKS.RIGHT_EYE];

  let closestRegion: FaceRegion = "cheek"; // Default to generic face touch
  let minDistance = Number.POSITIVE_INFINITY;

  // Check nose
  if (nose && (nose.visibility ?? 0) >= minVisibility) {
    const noseX = nose.x * videoWidth;
    const noseY = nose.y * videoHeight;
    const distance = Math.hypot(fingerX - noseX, fingerY - noseY);
    if (distance < minDistance) {
      minDistance = distance;
      closestRegion = "nose";
    }
  }

  // Check mouth region
  if (mouthLeft && mouthRight && (mouthLeft.visibility ?? 0) >= minVisibility && (mouthRight.visibility ?? 0) >= minVisibility) {
    const mouthCenterX = ((mouthLeft.x + mouthRight.x) / 2) * videoWidth;
    const mouthCenterY = ((mouthLeft.y + mouthRight.y) / 2) * videoHeight;
    const distance = Math.hypot(fingerX - mouthCenterX, fingerY - mouthCenterY);
    if (distance < minDistance) {
      minDistance = distance;
      closestRegion = "mouth";
    }
  }

  // Check eyes
  if (leftEye && (leftEye.visibility ?? 0) >= minVisibility) {
    const eyeX = leftEye.x * videoWidth;
    const eyeY = leftEye.y * videoHeight;
    const distance = Math.hypot(fingerX - eyeX, fingerY - eyeY);
    if (distance < minDistance) {
      minDistance = distance;
      closestRegion = "eye";
    }
  }

  if (rightEye && (rightEye.visibility ?? 0) >= minVisibility) {
    const eyeX = rightEye.x * videoWidth;
    const eyeY = rightEye.y * videoHeight;
    const distance = Math.hypot(fingerX - eyeX, fingerY - eyeY);
    if (distance < minDistance) {
      minDistance = distance;
      closestRegion = "eye";
    }
  }

  return closestRegion;
}

/**
 * Analyzes body posture by checking head forward position relative to shoulders.
 * Bad posture (slouching) is detected when the head is significantly forward of the shoulders.
 */
function analyzeBodyPosture(
  poseLandmarks: Landmark[],
  minVisibility: number,
  videoWidth: number,
  videoHeight: number
): PostureQuality {
  // Get key landmarks for posture analysis
  const nose = poseLandmarks[POSE_LANDMARKS.NOSE];
  const leftShoulder = poseLandmarks[POSE_LANDMARKS.LEFT_SHOULDER];
  const rightShoulder = poseLandmarks[POSE_LANDMARKS.RIGHT_SHOULDER];
  const leftEar = poseLandmarks[POSE_LANDMARKS.LEFT_EAR];
  const rightEar = poseLandmarks[POSE_LANDMARKS.RIGHT_EAR];

  // Default to good posture if we don't have enough landmarks
  const defaultResult: PostureQuality = {
    isBadPosture: false,
    headForwardAngle: null,
    postureConfidence: 0,
  };

  // Need at least shoulders and either nose or ears
  if (!leftShoulder || !rightShoulder || (leftShoulder.visibility ?? 0) < minVisibility || (rightShoulder.visibility ?? 0) < minVisibility) {
    return defaultResult;
  }

  // Calculate average shoulder position
  const shoulderX = ((leftShoulder.x + rightShoulder.x) / 2) * videoWidth;
  const shoulderY = ((leftShoulder.y + rightShoulder.y) / 2) * videoHeight;

  // Try to use ears first (more stable for side profile), fallback to nose
  let headX = 0;
  let headY = 0;
  let headConfidence = 0;

  if (leftEar && rightEar && (leftEar.visibility ?? 0) >= minVisibility && (rightEar.visibility ?? 0) >= minVisibility) {
    headX = ((leftEar.x + rightEar.x) / 2) * videoWidth;
    headY = ((leftEar.y + rightEar.y) / 2) * videoHeight;
    headConfidence = ((leftEar.visibility ?? 0) + (rightEar.visibility ?? 0)) / 2;
  } else if (nose && (nose.visibility ?? 0) >= minVisibility) {
    headX = nose.x * videoWidth;
    headY = nose.y * videoHeight;
    headConfidence = nose.visibility ?? 0;
  } else {
    return defaultResult;
  }

  // Calculate horizontal distance (how far forward the head is from shoulders)
  const horizontalDistance = Math.abs(headX - shoulderX);
  
  // Calculate vertical distance (head should be above shoulders)
  const verticalDistance = shoulderY - headY;
  
  // If head is not above shoulders, something is wrong with detection
  if (verticalDistance <= 0) {
    return defaultResult;
  }

  // Calculate angle from vertical
  // A perfectly upright posture would have the head directly above shoulders (0°)
  // Forward head posture increases this angle
  const angleRadians = Math.atan2(horizontalDistance, verticalDistance);
  const angleDegrees = (angleRadians * 180) / Math.PI;

  const isBadPosture = angleDegrees > BAD_POSTURE_ANGLE_THRESHOLD;

  const postureConfidence = Math.min(
    headConfidence,
    (leftShoulder.visibility ?? 0),
    (rightShoulder.visibility ?? 0)
  );

  return {
    isBadPosture,
    headForwardAngle: angleDegrees,
    postureConfidence,
  };
}

function averageLandmarks(landmarks: Landmark[], indices: number[], minVisibility: number, videoWidth: number, videoHeight: number): { x: number; y: number; visibility: number } | null {
  const collected: Landmark[] = [];

  for (const index of indices) {
    const landmark = landmarks[index];
    if (!landmark) continue;
    if ((landmark.visibility ?? 0) < minVisibility) continue;
    collected.push(landmark);
  }

  if (collected.length === 0) {
    return null;
  }

  const sum = collected.reduce<{ x: number; y: number; visibility: number }>(
    (acc, lm) => {
      acc.x += lm.x * videoWidth;
      acc.y += lm.y * videoHeight;
      acc.visibility += lm.visibility ?? 0;
      return acc;
    },
    { x: 0, y: 0, visibility: 0 }
  );

  return {
    x: sum.x / collected.length,
    y: sum.y / collected.length,
    visibility: sum.visibility / collected.length,
  };
}

export function analyzePosture(
  poseLandmarks: Landmark[],
  options: { minConfidence?: number; handLandmarks?: HandLandmark[][]; videoWidth?: number; videoHeight?: number } = {}
): PostureAssessment | null {
  const minVisibility = options.minConfidence ?? DEFAULT_MIN_VISIBILITY;
  const handLandmarks = options.handLandmarks ?? [];
  const videoWidth = options.videoWidth ?? 640;
  const videoHeight = options.videoHeight ?? 480;

  // Calculate face center from face landmarks (including mouth for nail biting detection)
  const faceIndices = [
    POSE_LANDMARKS.NOSE, 
    POSE_LANDMARKS.LEFT_EYE, 
    POSE_LANDMARKS.RIGHT_EYE, 
    POSE_LANDMARKS.LEFT_EAR, 
    POSE_LANDMARKS.RIGHT_EAR,
    POSE_LANDMARKS.MOUTH_LEFT,
    POSE_LANDMARKS.MOUTH_RIGHT
  ];
  const faceCenter = averageLandmarks(poseLandmarks, faceIndices, minVisibility, videoWidth, videoHeight);

  if (!faceCenter) {
    return null;
  }

  // Calculate reference length from shoulders or hips
  const shoulderIndices = [POSE_LANDMARKS.LEFT_SHOULDER, POSE_LANDMARKS.RIGHT_SHOULDER];
  const hipIndices = [POSE_LANDMARKS.LEFT_HIP, POSE_LANDMARKS.RIGHT_HIP];
  
  const midShoulder = averageLandmarks(poseLandmarks, shoulderIndices, minVisibility, videoWidth, videoHeight);
  const midHip = averageLandmarks(poseLandmarks, hipIndices, minVisibility, videoWidth, videoHeight);
  
  let referenceLength = 120;
  if (midHip && midShoulder) {
    referenceLength = Math.max(60, Math.hypot(midShoulder.x - midHip.x, midShoulder.y - midHip.y));
  } else if (midShoulder) {
    const leftShoulder = poseLandmarks[POSE_LANDMARKS.LEFT_SHOULDER];
    const rightShoulder = poseLandmarks[POSE_LANDMARKS.RIGHT_SHOULDER];
    if (leftShoulder && rightShoulder) {
      referenceLength = Math.max(60, Math.hypot(
        (leftShoulder.x - rightShoulder.x) * videoWidth,
        (leftShoulder.y - rightShoulder.y) * videoHeight
      ));
    }
  }

  let handToFaceDistanceRatio = Number.POSITIVE_INFINITY;
  let activeHand: "left" | "right" | null = null;
  let touchingFinger: PostureMetrics["touchingFinger"] = null;
  let faceRegion: FaceRegion = null;
  let handTouchingFace = false;
  let closestFingerX = 0;
  let closestFingerY = 0;

  const faceVisibility = faceCenter.visibility ?? 0;

  // Check finger landmarks first (more accurate)
  if (handLandmarks.length > 0) {
    for (let handIndex = 0; handIndex < handLandmarks.length; handIndex++) {
      const landmarks = handLandmarks[handIndex];
      if (!landmarks) continue;
      const handLabel = handIndex === 0 ? "left" : "right"; // MediaPipe typically returns left hand first

      // Check each fingertip
      for (const [fingerName, tipIndex] of Object.entries(FINGERTIP_INDICES)) {
        const fingertip = landmarks[tipIndex];
        if (!fingertip) continue;

        const fingertipX = fingertip.x * videoWidth;
        const fingertipY = fingertip.y * videoHeight;

        const distance = Math.hypot(fingertipX - faceCenter.x, fingertipY - faceCenter.y);
        const ratio = distance / referenceLength;

        if (ratio < handToFaceDistanceRatio) {
          handToFaceDistanceRatio = ratio;
          activeHand = handLabel;
          touchingFinger = fingerName as FingerName;
          closestFingerX = fingertipX;
          closestFingerY = fingertipY;
        }

        const withinShoulderWindow = !midShoulder || fingertipY <= midShoulder.y + referenceLength * SHOULDER_BUFFER;
        if (withinShoulderWindow && ratio <= FINGER_DISTANCE_THRESHOLD) {
          handTouchingFace = true;
        }
      }

      // Also check palm (wrist area) - landmark 0
      const palm = landmarks[0];
      if (palm) {
        const palmX = palm.x * videoWidth;
        const palmY = palm.y * videoHeight;
        const distance = Math.hypot(palmX - faceCenter.x, palmY - faceCenter.y);
        const ratio = distance / referenceLength;

        if (ratio < handToFaceDistanceRatio) {
          handToFaceDistanceRatio = ratio;
          activeHand = handLabel;
          touchingFinger = "palm";
        }

        const withinShoulderWindow = !midShoulder || palmY <= midShoulder.y + referenceLength * SHOULDER_BUFFER;
        if (withinShoulderWindow && ratio <= WRIST_DISTANCE_THRESHOLD) {
          handTouchingFace = true;
        }
      }
    }
  }

  // Fallback to wrist detection from pose landmarks if no hand landmarks
  if (!handLandmarks.length) {
    const wristIndices = [POSE_LANDMARKS.LEFT_WRIST, POSE_LANDMARKS.RIGHT_WRIST];
    
    for (const wristIndex of wristIndices) {
      const wrist = poseLandmarks[wristIndex];
      if (!wrist || (wrist.visibility ?? 0) < minVisibility) continue;

      const wristX = wrist.x * videoWidth;
      const wristY = wrist.y * videoHeight;
      const distance = Math.hypot(wristX - faceCenter.x, wristY - faceCenter.y);
      const ratio = distance / referenceLength;

      if (ratio < handToFaceDistanceRatio) {
        handToFaceDistanceRatio = ratio;
        activeHand = wristIndex === POSE_LANDMARKS.LEFT_WRIST ? "left" : "right";
        touchingFinger = "palm";
      }

      const withinShoulderWindow = !midShoulder || wristY <= midShoulder.y + referenceLength * SHOULDER_BUFFER;
      if (withinShoulderWindow && ratio <= WRIST_DISTANCE_THRESHOLD) {
        handTouchingFace = true;
      }
    }
  }

  if (!Number.isFinite(handToFaceDistanceRatio)) {
    handToFaceDistanceRatio = 1;
    activeHand = null;
    touchingFinger = null;
  }

  // Determine which part of the face is being touched
  if (handTouchingFace && closestFingerX !== 0 && closestFingerY !== 0) {
    faceRegion = determineFaceRegion(closestFingerX, closestFingerY, poseLandmarks, minVisibility, videoWidth, videoHeight);
  }

  // Classify specific behavior based on face region and finger
  let behavior: "nose-picking" | "nail-biting" | "face-touching" | null = null;
  if (handTouchingFace && faceRegion && touchingFinger) {
    if (faceRegion === "nose" && (touchingFinger === "index" || touchingFinger === "thumb" || touchingFinger === "pinky")) {
      behavior = "nose-picking";
    } else if (faceRegion === "mouth") {
      behavior = "nail-biting";
    } else {
      behavior = "face-touching";
    }
  }

  // Analyze body posture
  const postureQuality = analyzeBodyPosture(poseLandmarks, minVisibility, videoWidth, videoHeight);

  return {
    metrics: {
      handToFaceDistanceRatio,
      activeHand,
      touchingFinger,
      faceRegion,
    },
    handTouchingFace,
    confidence: faceVisibility,
    behavior,
    postureQuality,
  };
}
